import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Save,
  ArrowLeft,
  FolderOpen,
  TagIcon,
  Calendar,
  Download,
  Youtube,
} from "lucide-react";
import { usePromptStore } from "../../store/promptStore";
import { extractYouTubeVideoId } from "../YouTubeEmbed";
// MDEditor removed due to parse-numeric-range dependency issues
import DownloadSelectorModal from "./DownloadSelectorModal";

// Initial form state
const INITIAL_FORM_STATE = {
  title: "",
  description: "",
  content: "",
  youtube_video_id: "",
  download_url: "",
  download_button_text: "",
  category_id: "",
  is_featured: false,
  is_published: true,
  published_at: "",
  tags: [] as string[],
};

export default function PromptEditor() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const {
    selectedPrompt,
    categories,
    tags,
    loading,
    error,
    fetchPromptById,
    fetchCategories,
    fetchTags,
    createPrompt,
    updatePrompt,
    clearSelectedPrompt,
  } = usePromptStore();

  const [formData, setFormData] = useState({ ...INITIAL_FORM_STATE });

  const [previewMode, setPreviewMode] = useState(false);
  const [showDownloadSelector, setShowDownloadSelector] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // Fetch data on mount
  useEffect(() => {
    fetchCategories();
    fetchTags();

    if (id && id !== "new") {
      fetchPromptById(id);
    } else {
      // Clear selected prompt when creating a new one
      clearSelectedPrompt();
      setFormData({ ...INITIAL_FORM_STATE });
    }
  }, [id, fetchPromptById, fetchCategories, fetchTags, clearSelectedPrompt]);

  // Update form when selectedPrompt changes
  useEffect(() => {
    if (selectedPrompt && id !== "new") {
      setFormData({
        title: selectedPrompt.title,
        description: selectedPrompt.description || "",
        youtube_video_id: selectedPrompt.youtube_video_id || "",
        download_url: selectedPrompt.download_url || "",
        download_button_text: selectedPrompt.download_button_text || "",
        content: selectedPrompt.content,
        category_id: selectedPrompt.category_id || "",
        is_featured: selectedPrompt.is_featured,
        is_published: selectedPrompt.is_published,
        published_at: selectedPrompt.published_at
          ? new Date(selectedPrompt.published_at).toISOString().split("T")[0]
          : "",
        tags: selectedPrompt.tags || [],
      });
    }
  }, [selectedPrompt]);

  const validateForm = () => {
    const errors: Record<string, string> = {};

    if (!formData.title.trim()) {
      errors.title = "Title is required";
    }

    if (!formData.content.trim()) {
      errors.content = "Content is required";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setSaving(true);

      if (id && id !== "new") {
        await updatePrompt(id, formData);
      } else {
        await createPrompt({
          ...formData,
          deleted: false,
        });
      }

      // Reset form after successful submission
      setFormData({ ...INITIAL_FORM_STATE });

      navigate("/prompt-admin/prompts");
    } catch (error) {
      console.error("Error saving prompt:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: checked }));
  };

  const handleTagToggle = (tagId: string) => {
    setFormData((prev) => {
      const newTags = prev.tags.includes(tagId)
        ? prev.tags.filter((id) => id !== tagId)
        : [...prev.tags, tagId];

      return { ...prev, tags: newTags };
    });
  };

  const handleDownloadSelect = (url: string, fileName: string) => {
    setFormData((prev) => ({
      ...prev,
      download_url: url,
      download_button_text: prev.download_button_text || `Download ${fileName}`,
    }));
    setShowDownloadSelector(false);
  };

  if (loading && id) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#F98B3D]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center">
              <button
                onClick={() => navigate("/prompt-admin/prompts")}
                className="mr-4 text-gray-600 hover:text-gray-900"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <h1 className="text-2xl font-bold text-gray-900">
                {id ? "Edit Prompt" : "New Prompt"}
              </h1>
            </div>
            <button
              onClick={handleSubmit}
              disabled={saving}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#F98B3D] hover:bg-[#e07a2c] disabled:opacity-50"
            >
              <Save className="h-5 w-5 mr-2" />
              {saving ? "Saving..." : "Save Prompt"}
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-md mb-6">
            <p className="text-red-700">{error}</p>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Form */}
          <div className="lg:col-span-2">
            <div className="bg-white shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <form onSubmit={handleSubmit}>
                  <div className="space-y-6">
                    {/* Title */}
                    <div>
                      <label
                        htmlFor="title"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Title *
                      </label>
                      <input
                        type="text"
                        name="title"
                        id="title"
                        value={formData.title}
                        onChange={handleInputChange}
                        className={`mt-1 block w-full rounded-md shadow-sm ${
                          formErrors.title
                            ? "border-red-300"
                            : "border-gray-300"
                        } focus:border-[#F98B3D] focus:ring-[#F98B3D]`}
                      />
                      {formErrors.title && (
                        <p className="mt-1 text-sm text-red-600">
                          {formErrors.title}
                        </p>
                      )}
                    </div>

                    {/* Description */}
                    <div>
                      <label
                        htmlFor="description"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Description
                      </label>
                      <input
                        type="text"
                        name="description"
                        id="description"
                        value={formData.description}
                        onChange={handleInputChange}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#F98B3D] focus:ring-[#F98B3D]"
                      />
                    </div>

                    {/* YouTube Video ID */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        YouTube Video ID
                      </label>
                      <div className="flex items-start space-x-4">
                        <div className="flex-grow">
                          <input
                            type="text"
                            value={formData.youtube_video_id || ""}
                            onChange={(e) => {
                              const inputValue = e.target.value;
                              const videoId = extractYouTubeVideoId(inputValue);
                              setFormData({
                                ...formData,
                                youtube_video_id: videoId || inputValue || "",
                              });
                            }}
                            placeholder="e.g., dQw4w9WgXcQ"
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#F98B3D] focus:ring-[#F98B3D]"
                          />
                          <p className="mt-1 text-xs text-gray-500">
                            Enter the YouTube video URL or ID (we'll extract the
                            ID automatically)
                          </p>
                        </div>
                        <div className="flex-shrink-0 flex items-center">
                          <Youtube className="h-5 w-5 text-gray-400" />
                        </div>
                      </div>
                      {formData.youtube_video_id && (
                        <div className="mt-2 border border-gray-200 rounded-lg overflow-hidden">
                          <div className="aspect-video">
                            <iframe
                              width="100%"
                              height="100%"
                              src={`https://www.youtube.com/embed/${formData.youtube_video_id}`}
                              title="YouTube video player"
                              frameBorder="0"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                              allowFullScreen
                            ></iframe>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Content */}
                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <label
                          htmlFor="content"
                          className="block text-sm font-medium text-gray-700"
                        >
                          Content *
                        </label>
                        <div className="flex items-center">
                          <button
                            type="button"
                            onClick={() => setPreviewMode(false)}
                            className="text-sm text-[#F98B3D] hover:text-[#e07a2c]"
                          >
                            Edit
                          </button>
                        </div>
                      </div>

                      <textarea
                        value={formData.content}
                        onChange={(e) =>
                          setFormData({ ...formData, content: e.target.value })
                        }
                        rows={20}
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:ring-[#F98B3D] focus:border-[#F98B3D] font-mono text-sm"
                        placeholder="Enter your prompt content here using Markdown formatting..."
                      />
                      {formErrors.content && (
                        <p className="mt-1 text-sm text-red-600">
                          {formErrors.content}
                        </p>
                      )}
                      <p className="mt-1 text-sm text-gray-500">
                        Include an <code>## Overview</code> section after the
                        title and before the <code>## Task</code> section to
                        provide a brief description of what the prompt does.
                      </p>
                    </div>

                    {/* Download URL */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Downloadable Resource
                      </label>
                      <div className="flex items-start space-x-4">
                        <div className="flex-grow">
                          <input
                            type="text"
                            value={formData.download_url}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                download_url: e.target.value,
                              })
                            }
                            placeholder="Download URL"
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-[#F98B3D] focus:border-[#F98B3D]"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => setShowDownloadSelector(true)}
                          className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                        >
                          <Download className="h-5 w-5 mr-2" />
                          Browse
                        </button>
                      </div>
                      {formData.download_url && (
                        <div className="mt-2">
                          <label className="block text-sm font-medium text-gray-700 mt-4 mb-2">
                            Button Text
                          </label>
                          <input
                            type="text"
                            value={formData.download_button_text}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                download_button_text: e.target.value,
                              })
                            }
                            placeholder="Download Button Text"
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-[#F98B3D] focus:border-[#F98B3D]"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Category */}
            <div className="bg-white shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg font-medium leading-6 text-gray-900 mb-4">
                  <FolderOpen className="inline-block h-5 w-5 mr-2 text-gray-500" />
                  Category
                </h3>
                <select
                  name="category_id"
                  id="category_id"
                  value={formData.category_id}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#F98B3D] focus:ring-[#F98B3D]"
                >
                  <option value="">Select a category</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Tags */}
            <div className="bg-white shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg font-medium leading-6 text-gray-900 mb-4">
                  <TagIcon className="inline-block h-5 w-5 mr-2 text-gray-500" />
                  Tags
                </h3>
                <div className="space-y-2">
                  {tags.length === 0 ? (
                    <p className="text-sm text-gray-500">No tags available</p>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {tags.map((tag) => (
                        <button
                          key={tag.id}
                          type="button"
                          onClick={() => handleTagToggle(tag.id)}
                          className={`inline-flex items-center px-3 py-1 rounded-full text-sm ${
                            formData.tags.includes(tag.id)
                              ? "bg-[#F98B3D] text-white"
                              : "bg-gray-100 text-gray-800 hover:bg-gray-200"
                          }`}
                        >
                          {tag.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Publishing Options */}
            <div className="bg-white shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg font-medium leading-6 text-gray-900 mb-4">
                  Publishing Options
                </h3>
                <div className="space-y-4">
                  <div className="flex items-center">
                    <input
                      id="is_published"
                      name="is_published"
                      type="checkbox"
                      checked={formData.is_published}
                      onChange={handleCheckboxChange}
                      className="h-4 w-4 text-[#F98B3D] focus:ring-[#F98B3D] border-gray-300 rounded"
                    />
                    <label
                      htmlFor="is_published"
                      className="ml-2 block text-sm text-gray-900"
                    >
                      Published
                    </label>
                  </div>

                  <div className="mt-4">
                    <label
                      htmlFor="published_at"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Published Date
                    </label>
                    <div className="mt-1 relative rounded-md shadow-sm">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Calendar className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="date"
                        id="published_at"
                        name="published_at"
                        value={formData.published_at || ""}
                        onChange={handleInputChange}
                        className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:ring-[#F98B3D] focus:border-[#F98B3D]"
                      />
                    </div>
                    <p className="mt-1 text-xs text-gray-500 max-w-xs">
                      Set the date when this prompt was or will be published
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Download Selector Modal */}
      <DownloadSelectorModal
        isOpen={showDownloadSelector}
        onClose={() => setShowDownloadSelector(false)}
        onSelect={handleDownloadSelect}
      />
    </div>
  );
}
